// Утилиты для расчета времени с учетом рабочих часов и выходных

/**
 * Константы рабочего времени
 */
const WORK_START_HOUR = 9;  // Начало рабочего дня (9:00)
const WORK_END_HOUR = 18;   // Конец рабочего дня (18:00)
const CUTOFF_HOUR = 13;     // После 13:00 считаем со следующего дня
const WORK_HOURS_PER_DAY = WORK_END_HOUR - WORK_START_HOUR; // 9 часов

/**
 * Проверяет является ли день рабочим (Пн-Пт)
 */
const isWorkingDay = (date) => {
  const day = date.getDay();
  return day >= 1 && day <= 5; // 1 = Понедельник, 5 = Пятница
};

/**
 * Получает начало следующего рабочего дня
 */
const getNextWorkingDay = (date) => {
  const next = new Date(date);
  next.setDate(next.getDate() + 1);
  next.setHours(WORK_START_HOUR, 0, 0, 0);

  // Пропускаем выходные
  while (!isWorkingDay(next)) {
    next.setDate(next.getDate() + 1);
  }

  return next;
};

/**
 * Нормализует дату к рабочему времени
 * Если дата в нерабочее время - переносит на начало следующего рабочего дня
 */
const normalizeToWorkingHours = (date) => {
  const normalized = new Date(date);
  const hour = normalized.getHours();

  // Если не рабочий день - переносим на следующий рабочий день
  if (!isWorkingDay(normalized)) {
    return getNextWorkingDay(normalized);
  }

  // Если позже 13:00 - переносим на следующий рабочий день
  if (hour >= CUTOFF_HOUR) {
    return getNextWorkingDay(normalized);
  }

  // Если раньше 9:00 - переносим на 9:00 этого же дня
  if (hour < WORK_START_HOUR) {
    normalized.setHours(WORK_START_HOUR, 0, 0, 0);
    return normalized;
  }

  // Если позже 18:00 (но раньше 13:00 следующего дня) - переносим на следующий день
  if (hour >= WORK_END_HOUR) {
    return getNextWorkingDay(normalized);
  }

  // Время в пределах рабочего дня (9:00-13:00)
  return normalized;
};

/**
 * Вычисляет рабочие часы между двумя датами
 * @param {Date} startDate - начальная дата
 * @param {Date} endDate - конечная дата
 * @returns {number} - количество рабочих часов
 */
export const calculateWorkingHours = (startDate, endDate) => {
  if (!startDate || !endDate) return 0;

  let start = normalizeToWorkingHours(new Date(startDate));
  const end = new Date(endDate);

  let totalHours = 0;
  let iterations = 0;
  const maxIterations = 1000; // Защита от бесконечного цикла

  while (start < end && iterations < maxIterations) {
    iterations++;

    // Если не рабочий день - пропускаем
    if (!isWorkingDay(start)) {
      start = getNextWorkingDay(start);
      continue;
    }

    // Определяем конец рабочего дня
    const dayEnd = new Date(start);
    dayEnd.setHours(WORK_END_HOUR, 0, 0, 0);

    // Если конец периода в этот же рабочий день
    if (end <= dayEnd) {
      const diff = end - start;
      totalHours += diff / (1000 * 60 * 60);
      break;
    }

    // Иначе считаем до конца рабочего дня
    const diff = dayEnd - start;
    totalHours += diff / (1000 * 60 * 60);

    // Переходим к следующему рабочему дню
    start = getNextWorkingDay(start);
  }

  return totalHours;
};

/**
 * Форматирует количество рабочих часов в человекочитаемый вид
 */
export const formatWorkingHours = (hours) => {
  if (typeof hours !== 'number' || isNaN(hours) || hours < 0) {
    return 'N/A';
  }

  const totalHours = Math.abs(hours);

  if (totalHours < 1) {
    // Меньше часа - показываем в минутах
    return `${Math.round(totalHours * 60)} мин`;
  }

  // Считаем рабочие дни (по 9 часов)
  const workingDays = Math.floor(totalHours / WORK_HOURS_PER_DAY);
  const remainingHours = totalHours % WORK_HOURS_PER_DAY;

  if (workingDays > 0) {
    const h = Math.floor(remainingHours);
    const m = Math.round((remainingHours - h) * 60);

    if (h > 0 && m > 0) {
      return `${workingDays}рд ${h}ч ${m}м`;
    } else if (h > 0) {
      return `${workingDays}рд ${h}ч`;
    } else if (m > 0) {
      return `${workingDays}рд ${m}м`;
    } else {
      return `${workingDays}рд`;
    }
  } else {
    // Меньше рабочего дня
    const h = Math.floor(totalHours);
    const m = Math.round((totalHours - h) * 60);
    return m > 0 ? `${h}ч ${m}м` : `${h}ч`;
  }
};

/**
 * Вычисляет рабочие часы для массива событий таймлайна
 */
export const calculateWorkingHoursForTimeline = (events) => {
  if (!events || events.length === 0) return [];

  const durations = [];

  for (let i = 0; i < events.length - 1; i++) {
    const currentEvent = events[i];
    const nextEvent = events[i + 1];

    const workingHours = calculateWorkingHours(currentEvent.timestamp, nextEvent.timestamp);

    durations.push({
      stage: currentEvent.stageName,
      stageId: currentEvent.stage,
      startTime: currentEvent.timestamp,
      endTime: nextEvent.timestamp,
      workingHours: workingHours,
      workingHoursFormatted: formatWorkingHours(workingHours)
    });
  }

  // Последняя стадия (если заказ еще не завершен)
  if (events.length > 0) {
    const lastEvent = events[events.length - 1];
    const now = new Date();
    const workingHours = calculateWorkingHours(lastEvent.timestamp, now);

    durations.push({
      stage: lastEvent.stageName,
      stageId: lastEvent.stage,
      startTime: lastEvent.timestamp,
      endTime: now,
      workingHours: workingHours,
      workingHoursFormatted: formatWorkingHours(workingHours),
      isCurrentStage: true
    });
  }

  return durations;
};

/**
 * Вычисляет общее рабочее время от первой до последней стадии
 */
export const calculateTotalWorkingHours = (events) => {
  if (!events || events.length === 0) return null;

  const firstEvent = events[0];
  const lastEvent = events[events.length - 1];

  const workingHours = calculateWorkingHours(firstEvent.timestamp, lastEvent.timestamp);

  return {
    workingHours: workingHours,
    workingHoursFormatted: formatWorkingHours(workingHours),
    startTime: firstEvent.timestamp,
    endTime: lastEvent.timestamp
  };
};
